// Partner form (create / edit). Used standalone in PartnersTab and as a modal
// inline-create from ServiceForm.
import { useState } from 'react';
import { toast } from 'sonner';

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default function PartnerForm({ initial, onSave, onCancel, asModal = false }) {
  const [form, setForm] = useState(() => ({
    slug: initial?.slug || '',
    name: initial?.name || '',
    logo_url: initial?.logo_url || '',
    website: initial?.website || '',
    contact_email: initial?.contact_email || '',
    description: initial?.description || '',
    available_cities: initial?.available_cities || [],
    is_active: initial?.is_active ?? true,
  }));
  const [cityInput, setCityInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slugTouched, setSlugTouched] = useState(Boolean(initial?.slug));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onNameChange = (val) => {
    update('name', val);
    if (!slugTouched) update('slug', slugify(val));
  };

  const addCity = () => {
    const v = cityInput.trim();
    if (!v) return;
    update('available_cities', [...new Set([...form.available_cities, v])]);
    setCityInput('');
  };
  const removeCity = (c) =>
    update('available_cities', form.available_cities.filter((x) => x !== c));

  const submit = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        slug: form.slug,
        name: form.name,
        logo_url: form.logo_url || null,
        website: form.website || null,
        contact_email: form.contact_email || null,
        description: form.description,
        available_cities: form.available_cities,
        ...(initial ? { is_active: form.is_active } : {}),
      };
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ label, hint, children }) => (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  const inputCls =
    'w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#2B3A67] focus:outline-none';

  const body = (
    <div className="space-y-4">
      <Field label="Display Name">
        <input
          data-testid="partner-form-name"
          value={form.name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="AromaThai"
          className={inputCls}
        />
      </Field>

      <Field label="Slug" hint="URL-safe identifier. Auto-generated from name; edit if needed.">
        <input
          data-testid="partner-form-slug"
          value={form.slug}
          onChange={(e) => {
            setSlugTouched(true);
            update('slug', slugify(e.target.value));
          }}
          placeholder="aromathai"
          className={inputCls}
          disabled={Boolean(initial)}
        />
      </Field>

      <Field label="Logo URL">
        <input
          data-testid="partner-form-logo"
          value={form.logo_url}
          onChange={(e) => update('logo_url', e.target.value)}
          placeholder="https://…/logo.png"
          className={inputCls}
        />
        {form.logo_url && (
          <img
            src={form.logo_url}
            alt="logo preview"
            className="w-16 h-16 rounded-xl object-cover mt-2 border border-gray-200"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            referrerPolicy="no-referrer"
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Website">
          <input
            data-testid="partner-form-website"
            value={form.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://…"
            className={inputCls}
          />
        </Field>
        <Field label="Contact Email">
          <input
            data-testid="partner-form-email"
            type="email"
            value={form.contact_email}
            onChange={(e) => update('contact_email', e.target.value)}
            placeholder="ops@…"
            className={inputCls}
          />
        </Field>
      </div>

      <Field label="Description">
        <textarea
          data-testid="partner-form-description"
          rows={3}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          className={inputCls}
        />
      </Field>

      <Field label="Available Cities">
        <div className="flex gap-2 mb-2">
          <input
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCity();
              }
            }}
            placeholder="Mumbai"
            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
          />
          <button
            type="button"
            onClick={addCity}
            data-testid="partner-form-add-city"
            className="px-4 py-2 bg-[#2B3A67] text-white font-bold text-sm rounded-xl"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.available_cities.map((c) => (
            <span
              key={c}
              className="text-xs font-bold px-3 py-1 bg-gray-100 rounded-full flex items-center gap-1"
            >
              {c}
              <button
                onClick={() => removeCity(c)}
                className="text-[#E54562]"
                data-testid={`partner-form-remove-city-${c}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </Field>

      {initial && (
        <label className="flex items-center gap-3 text-sm bg-gray-50 rounded-xl p-3">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update('is_active', e.target.checked)}
            data-testid="partner-form-active"
          />
          <div>
            <p className="font-bold">Active</p>
            <p className="text-xs text-gray-500">
              When off, this partner and their services are hidden from the public storefront
              (data preserved).
            </p>
          </div>
        </label>
      )}

      <div className="flex gap-2 pt-3">
        <button
          onClick={onCancel}
          type="button"
          className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="partner-form-submit"
          className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50"
        >
          {submitting ? 'Saving...' : initial ? 'Save Changes' : 'Create Partner'}
        </button>
      </div>
    </div>
  );

  if (!asModal) return body;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
      data-testid="partner-form-modal"
    >
      <div
        className="bg-white w-full max-w-md rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-extrabold text-[#2B3A67]">
            {initial ? 'Edit Partner' : 'New Partner'}
          </h2>
          <button
            onClick={onCancel}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"
          >
            <i className="ph-bold ph-x" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}
